import { useClipboard } from '@calendly/clipboard';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { Button } from '@calendly/ui/components/button';
import { ButtonGroup } from '@calendly/ui/components/button-group';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@calendly/ui/components/dropdown-menu';
import { IconCalendarPlus } from '@calendly/ui/components/glyphs/IconCalendarPlus';
import { IconChevronDown } from '@calendly/ui/components/glyphs/IconChevronDown';
import { IconChevronUp } from '@calendly/ui/components/glyphs/IconChevronUp';
import { IconCircleCheck } from '@calendly/ui/components/glyphs/IconCircleCheck';
import { IconCircleDashedNumber1 } from '@calendly/ui/components/glyphs/IconCircleDashedNumber1';
import { IconLink } from '@calendly/ui/components/glyphs/IconLink';
import { IconMail } from '@calendly/ui/components/glyphs/IconMail';
import { IconShare3 } from '@calendly/ui/components/glyphs/IconShare3';
import { IconButton } from '@calendly/ui/components/icon-button';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { colorIconSuccess } from '@calendly/ui/theme';
import { cx } from '@linaria/core';
import { useState } from 'react';
import { createBookingLink } from 'shared/utils/create_booking_link';
import { EditorToast } from './editor_toast';
import { HeaderMoreMenu } from './header_more_menu';
import { useCreateEventTypeSingleUseLink } from '../hooks/use_create_single_use_link';
import { PRIMARY_ACTIONS_PLACEMENT, useEditorPrimaryActions } from '../hooks/use_editor_primary_actions';
const actionsCss = "a1mctyo7";
const actionsFooterCss = "a1i7x8qz";

// Equal-width footer slots for copy-link split + publish (Figma mobile footer).
const footerSlotCss = "f65qxqp";
const footerFillCss = "f16j80q3";
const footerButtonGroupCss = "flkw1n9";
const footerCopyLinkButtonCss = "f1hqe6ue";
const footerPublishButtonCss = "fpqkw4b";
const publishAnchorCss = "p1d1na5u";

// CUI Button has no success appearance; className overrides lose to its stylesheet.
// Transient "Saved" flash: BareButton sized like a medium Button (CUI tokens).
const justSavedButtonCss = "j1a1s7t9";
const CopyLinkSplit = ({
  eventType,
  isLive,
  isFooter,
  testIdPrefix
}) => {
  const [ClipboardWrapper, clipboardRef, isCopied] = useClipboard(eventType.booking_url);
  const {
    create: createSingleUseLink,
    isLoading: isCreatingSingleUseLink
  } = useCreateEventTypeSingleUseLink(eventType);
  const [isSecondaryActionsMenuOpen, setIsSecondaryActionsMenuOpen] = useState(false);
  const handleCopySingleUseLink = async () => {
    setIsSecondaryActionsMenuOpen(false);
    const response = await createSingleUseLink();
    if (response?.data) {
      try {
        await navigator.clipboard.writeText(createBookingLink(response.data));
      } catch {
        console.warn('Clipboard API not available');
      }
    }
  };
  return <ClipboardWrapper>
      <ButtonGroup className={isFooter ? footerButtonGroupCss : undefined}>
        <Button ref={clipboardRef} appearance="secondary-outline" size="medium" disabled={!isLive} className={isFooter ? footerCopyLinkButtonCss : undefined} data-testid={`${testIdPrefix}-copy-link`}>
          <UiIcon icon={IconLink} size="small" title="" />
          {isCopied ? I18n.t('publisher.event_type_editor_crud.header.copied') : I18n.t('publisher.event_type_editor_crud.header.copy_link')}
        </Button>
        <DropdownMenu isOpen={isSecondaryActionsMenuOpen} onClose={() => setIsSecondaryActionsMenuOpen(false)}>
          <DropdownMenuTrigger>
            <IconButton appearance="secondary-outline" size="medium" icon={isSecondaryActionsMenuOpen ? IconChevronUp : IconChevronDown} aria-label={I18n.t('publisher.event_type_editor_crud.header.secondary_actions.label')} disabled={!isLive} onClick={() => setIsSecondaryActionsMenuOpen(isOpen => !isOpen)} data-testid={`${testIdPrefix}-copy-link-menu-trigger`} />
          </DropdownMenuTrigger>
          <DropdownMenuContent placement={isFooter ? 'top end' : 'bottom end'}>
            <DropdownMenuItem onSelect={handleCopySingleUseLink} disabled={isCreatingSingleUseLink} data-testid={`${testIdPrefix}-copy-single-use-link`}>
              <UiIcon icon={IconCircleDashedNumber1} size="small" title="" />
              {I18n.t('publisher.event_type_editor_crud.header.secondary_actions.copy_single_use_link')}
            </DropdownMenuItem>
            <DropdownMenuItem disabled data-testid={`${testIdPrefix}-book-meeting`}>
              <UiIcon icon={IconCalendarPlus} size="small" title="" />
              {I18n.t('publisher.event_type_editor_crud.header.secondary_actions.book_meeting')}
            </DropdownMenuItem>
            <DropdownMenuItem disabled data-testid={`${testIdPrefix}-offer-time-slots`}>
              <UiIcon icon={IconMail} size="small" title="" />
              {I18n.t('publisher.event_type_editor_crud.header.secondary_actions.offer_time_slots')}
            </DropdownMenuItem>
            <DropdownMenuItem disabled data-testid={`${testIdPrefix}-share-availability`}>
              <UiIcon icon={IconShare3} size="small" title="" />
              {I18n.t('publisher.event_type_editor_crud.header.secondary_actions.share_availability')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>
    </ClipboardWrapper>;
};

// Copy / share / publish — `placement` is owned by the layout (header vs sticky footer).
export const EditorPrimaryActions = ({
  eventType,
  placement = PRIMARY_ACTIONS_PLACEMENT.header
}) => {
  const isFooter = placement === PRIMARY_ACTIONS_PLACEMENT.footer;
  const testIdPrefix = isFooter ? 'editor-footer' : 'editor-header';
  const {
    isLive,
    isSubmitting,
    isActivating,
    isToastVisible,
    isPublishButtonDisabled,
    showJustSavedFlash,
    getPublishButtonLabel,
    handlePublishClick,
    handleToastDismiss
  } = useEditorPrimaryActions(eventType, {
    placement
  });
  const publishButton = showJustSavedFlash ? <BareButton type="button" className={cx(justSavedButtonCss, isFooter && footerPublishButtonCss)} data-testid={`${testIdPrefix}-publish`}>
        <UiIcon icon={IconCircleCheck} size="small" color={colorIconSuccess} title="" />
        {getPublishButtonLabel()}
      </BareButton> : <Button appearance="primary" size="medium" className={isFooter ? footerPublishButtonCss : undefined} data-testid={`${testIdPrefix}-publish`} loading={isSubmitting || isActivating} disabled={isPublishButtonDisabled} onClick={handlePublishClick}>
        {getPublishButtonLabel()}
      </Button>;
  const copyLink = <CopyLinkSplit eventType={eventType} isLive={isLive} isFooter={isFooter} testIdPrefix={testIdPrefix} />;
  const publishControl = isFooter ? publishButton : <div className={publishAnchorCss}>
        {publishButton}
        {isToastVisible && <EditorToast message={I18n.t('publisher.event_type_editor_crud.header.live_page_updated')} onDismiss={handleToastDismiss} />}
      </div>;
  return <>
      <div className={cx(actionsCss, isFooter && actionsFooterCss)} data-testid={isFooter ? 'editor-action-footer' : undefined}>
        {isFooter ? <div className={footerSlotCss}>
              <div className={footerFillCss}>{copyLink}</div>
            </div> : copyLink}
        {!isFooter && <HeaderMoreMenu eventType={eventType} isPhone={false} />}
        {isFooter ? <div className={footerSlotCss}>
              <div className={footerFillCss}>{publishControl}</div>
            </div> : publishControl}
      </div>
      {isFooter && isToastVisible && <EditorToast message={I18n.t('publisher.event_type_editor_crud.header.live_page_updated')} onDismiss={handleToastDismiss} />}
    </>;
};

require("./editor_primary_actions.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./editor_primary_actions.js");